import { useState } from 'react'
import SearchableSelect from '../SearchableSelect'

interface GoodsTabProps {
  jobData: any
  setJobData: (data: any) => void
  readOnly?: boolean
}

interface Goods {
  id?: string
  item_no: number
  no_of_packs?: number
  package_type?: string
  gross_weight?: number
  net_weight?: number
  volume_m3?: number
  weight_unit: string
  goods_description?: string
  marks_and_numbers?: string
  commodity_code?: string
  fob_value?: number
}

export default function GoodsTab({ jobData, setJobData, readOnly = false }: GoodsTabProps) {
  const [editingItem, setEditingItem] = useState<number | null>(null)
  const [newGoods, setNewGoods] = useState<Goods>({
    item_no: 1,
    weight_unit: 'KG',
  })

  const goods = jobData.goods || []

  const addGoods = () => {
    if (!newGoods.goods_description) {
      alert('Please enter goods description')
      return
    }

    const itemNo = goods.length > 0 ? Math.max(...goods.map((g: any) => g.item_no)) + 1 : 1

    setJobData({
      ...jobData,
      goods: [...goods, { ...newGoods, item_no: itemNo }]
    })

    // Reset form
    setNewGoods({
      item_no: itemNo + 1,
      weight_unit: 'KG',
    })
  }

  const removeGoods = (index: number) => {
    setJobData({
      ...jobData,
      goods: goods.filter((_: any, i: number) => i !== index)
    })
  }

  const updateGoods = (index: number, field: string, value: any) => {
    const updated = [...goods]
    updated[index] = { ...updated[index], [field]: value }
    setJobData({ ...jobData, goods: updated })
  }

  return (
    <div className="space-y-6">
      {/* Goods List */}
      <div>
        <h3 className="font-medium text-gray-900 dark:text-white mb-4">Goods Items</h3>
        {goods.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">
                    Item
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">
                    Description
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">
                    Packs
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">
                    Weight (KG)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">
                    Volume (M³)
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {goods.map((item: any, idx: number) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{item.item_no}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {editingItem === idx ? (
                        <input
                          type="text"
                          value={item.goods_description || ''}
                          onChange={(e) => updateGoods(idx, 'goods_description', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                        />
                      ) : (
                        item.goods_description
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {editingItem === idx ? (
                        <input
                          type="number"
                          value={item.no_of_packs || ''}
                          onChange={(e) => updateGoods(idx, 'no_of_packs', e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                        />
                      ) : (
                        item.no_of_packs
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {editingItem === idx ? (
                        <input
                          type="number"
                          step="0.001"
                          value={item.gross_weight || ''}
                          onChange={(e) => updateGoods(idx, 'gross_weight', e.target.value ? parseFloat(e.target.value) : null)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                        />
                      ) : (
                        item.gross_weight
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {editingItem === idx ? (
                        <input
                          type="number"
                          step="0.0001"
                          value={item.volume_m3 || ''}
                          onChange={(e) => updateGoods(idx, 'volume_m3', e.target.value ? parseFloat(e.target.value) : null)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                        />
                      ) : (
                        item.volume_m3
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm space-x-2">
                      {editingItem === idx ? (
                        <>
                          <button
                            onClick={() => setEditingItem(null)}
                            className="text-green-600 hover:text-green-900 dark:hover:text-green-400"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingItem(null)
                              removeGoods(idx)
                            }}
                            className="text-red-600 hover:text-red-900 dark:hover:text-red-400"
                          >
                            Delete
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditingItem(idx)}
                            className="text-blue-600 hover:text-blue-900 dark:hover:text-blue-400"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => removeGoods(idx)}
                            className="text-red-600 hover:text-red-900 dark:hover:text-red-400"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add New Goods */}
      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg space-y-4">
        <h3 className="font-medium text-gray-900 dark:text-white">Add New Goods Item</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Goods Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={newGoods.goods_description || ''}
            onChange={(e) => setNewGoods({ ...newGoods, goods_description: e.target.value })}
            disabled={readOnly}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
            rows={2}
            placeholder="e.g., Electronic components, furniture, etc."
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Number of Packs
            </label>
            <input
              type="number"
              value={newGoods.no_of_packs || ''}
              onChange={(e) => setNewGoods({ ...newGoods, no_of_packs: e.target.value ? parseInt(e.target.value) : undefined })}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
              placeholder="e.g., 50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Package Type
            </label>
            <input
              type="text"
              value={newGoods.package_type || ''}
              onChange={(e) => setNewGoods({ ...newGoods, package_type: e.target.value })}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
              placeholder="e.g., Carton, Pallet, etc."
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Gross Weight
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                step="0.001"
                value={newGoods.gross_weight || ''}
                onChange={(e) => setNewGoods({ ...newGoods, gross_weight: e.target.value ? parseFloat(e.target.value) : undefined })}
                disabled={readOnly}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
                placeholder="0.000"
              />
              <SearchableSelect
                options={[
                  { id: 'KG', label: 'KG' },
                  { id: 'LB', label: 'LB' }
                ]}
                value={newGoods.weight_unit || 'KG'}
                onChange={(value) => setNewGoods({ ...newGoods, weight_unit: value })}
                disabled={readOnly}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Net Weight
            </label>
            <input
              type="number"
              step="0.001"
              value={newGoods.net_weight || ''}
              onChange={(e) => setNewGoods({ ...newGoods, net_weight: e.target.value ? parseFloat(e.target.value) : undefined })}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
              placeholder="0.000"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Volume (M³)
            </label>
            <input
              type="number"
              step="0.0001"
              value={newGoods.volume_m3 || ''}
              onChange={(e) => setNewGoods({ ...newGoods, volume_m3: e.target.value ? parseFloat(e.target.value) : undefined })}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
              placeholder="0.0000"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Commodity Code (HS)
            </label>
            <input
              type="text"
              value={newGoods.commodity_code || ''}
              onChange={(e) => setNewGoods({ ...newGoods, commodity_code: e.target.value })}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
              placeholder="e.g., 8471.30"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Marks and Numbers
          </label>
          <input
            type="text"
            value={newGoods.marks_and_numbers || ''}
            onChange={(e) => setNewGoods({ ...newGoods, marks_and_numbers: e.target.value })}
            disabled={readOnly}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
            placeholder="e.g., LOT-001, BOX-A1, etc."
          />
        </div>

        <button
          onClick={addGoods}
          disabled={readOnly}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          Add Goods Item
        </button>
      </div>
    </div>
  )
}
